import type { DataScope } from "../workflowData/models";
import { z } from "zod";
import {
    boxTaskSettingsSchema, noteWorkflowSchema, noteWorkflowsDocumentSchema, outcomeSchema,
    stepSchema, subflowSchema, triggerSchema, validateNoteWorkflowsDocument
} from "../../components/NoteWorkflows/noteWorkflowsSchema";
import { workflowDraftSchema } from "../workflowBuilder/models";
import type { NoteWorkflow } from "../../components/NoteWorkflows/noteWorkflowsTypes";
import { shiftHandoverDefinitionSchema } from "./handoverModels";
import type { ShiftHandoverDefinition } from "./handoverModels";

// An existing box without per-box settings must retain its original permissions.
// The native UI uses undefined; the admin API represents this explicitly as null.
const permissions = boxTaskSettingsSchema.nullish().transform(value => value ?? undefined);
const nativeNodeFields = { title: stepSchema.shape.title.default(""), display_mode: z.literal("content").default("content"), task_settings: permissions,
    context_selectors: stepSchema.shape.context_selectors.default([]) };
const nativeStepSchema = stepSchema.extend(nativeNodeFields).strict();
const nativeOutcomeSchema = outcomeSchema.extend(nativeNodeFields).strict();
const nativeSubflowSchema = subflowSchema.extend({ ...nativeNodeFields, steps: z.array(nativeStepSchema) }).strict();

// Native organization proposals must never silently discard personal-builder fields.
export const organizationWorkflowSchema = noteWorkflowSchema.extend({
    trigger: triggerSchema.extend({ title: triggerSchema.shape.title.default("") }).strict(),
    steps: z.array(nativeStepSchema),
    outcomes: z.array(nativeOutcomeSchema),
    subflows: z.array(nativeSubflowSchema).default([]),
    view_all_boxes: z.boolean().default(false)
}).strict().superRefine((workflow, context) => {
    const error = validateNoteWorkflowsDocument({ schema_version: 1, workflows: [workflow] });
    if (error) context.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const pendingSignPermissionSchema = z.object({ node_id: z.string(), member_id: z.string() }).strict();
export const organizationChatResponseSchema = z.object({
    workflow: organizationWorkflowSchema.nullable(),
    intent: z.enum(["build", "preview", "clarify", "schedule", "handover"]),
    handover: shiftHandoverDefinitionSchema.nullable().optional(),
    reply: z.string(),
    scheduled_draft: workflowDraftSchema.refine(draft => draft.workflow.kind === "reminder", "An admin task must have a schedule").nullable().optional(),
    preview: workflowDraftSchema.shape.preview,
    pending_sign_permissions: z.array(pendingSignPermissionSchema)
}).superRefine((response, context) => {
    if ((response.intent === "schedule") !== Boolean(response.scheduled_draft)) {
        context.addIssue({ code: "custom", path: ["scheduled_draft"], message: "A schedule response must include its task draft" });
    }
    if (response.intent === "handover" && !response.handover) {
        context.addIssue({ code: "custom", path: ["handover"], message: "A shift handover response must include its rule definition" });
    }
    if (response.handover && (response.workflow || response.scheduled_draft)) {
        context.addIssue({ code: "custom", path: ["handover"], message: "A shift handover cannot also be a document or scheduled task" });
    }
});
export const organizationSaveResponseSchema = z.object({ note_workflows: noteWorkflowsDocumentSchema });
export const organizationPreviewSelectionSchema = z.object({
    box: workflowDraftSchema.shape.preview.unwrap().shape.boxes.element
});

export type PendingSignPermission = z.infer<typeof pendingSignPermissionSchema>;
export type OrganizationChatResponse = z.infer<typeof organizationChatResponseSchema>;
export type OrganizationPreview = NonNullable<OrganizationChatResponse["preview"]>;
export type OrganizationPreviewBox = OrganizationPreview["boxes"][number];
export type OrganizationChatMessage = { role: "user" | "assistant"; content: string };
export type OrganizationChatRequest = {
    request_id?: string;
    timezone?: string;
    workflow: NoteWorkflow | null;
    handover?: ShiftHandoverDefinition | null;
    message: string;
    messages: OrganizationChatMessage[];
    mode: "build" | "preview";
    language: string;
    pending_sign_permissions: PendingSignPermission[];
    project_id?: string | null;
    data_scope?: DataScope | null;
};

/** Retain the newest conversation context within the endpoint's bounded history. */
export function boundedChatHistory(messages: readonly OrganizationChatMessage[]): OrganizationChatMessage[] {
    const result: OrganizationChatMessage[] = [];
    let remaining = 24000;
    for (const message of messages.slice(-20).reverse()) {
        if (!remaining) break;
        const content = message.content.slice(-remaining);
        result.unshift({ role: message.role, content });
        remaining -= content.length;
    }
    return result;
}
